import { skipToken } from '@reduxjs/toolkit/dist/query';
import useLocalStorage from 'hooks/useLocalStorage';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useMemo } from 'react';
import { useGetStockLocationsClientV2Query } from 'store/slices/clientV2/stockLocations';
import {
  DISABLE_PAGE_LIMIT,
  GenericResource,
  QueryFilterOperators,
} from 'types/api';
import { StockLocationSchema } from 'types/clientV2/stockLocations';
import { DatagridFilterOperatorsValues } from 'types/datagrid';
import { StockLocationType } from 'types/inventory';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import { WarehouseStockLocation } from 'views/warehouse/WarehouseStockLocationContext';

export const useGetPickingLocations = (stockLotLocationsIds: string[]) => {
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const firstHalfIds = useMemo(
    () =>
      stockLotLocationsIds.slice(0, Math.ceil(stockLotLocationsIds.length / 2)),
    [stockLotLocationsIds]
  );
  const secondHalfIds = useMemo(
    () =>
      stockLotLocationsIds.slice(Math.ceil(stockLotLocationsIds.length / 2)),
    [stockLotLocationsIds]
  );

  const {
    data: stockLocationsResponse,
    isFetching: isFetchingStockLocations,
    isLoading: isLoadingStockLocations,
    refetch: refetchStockLocations,
  } = useServerSideQuery<StockLocationSchema, WarehouseStockLocation>(
    useGetStockLocationsClientV2Query,
    {
      pageSize: DISABLE_PAGE_LIMIT,
      schema: [
        'id',
        'facility',
        'name',
        'externalName',
        'locationType',
        'parent',
      ],
      filters: [
        {
          field: 'facility',
          operator: DatagridFilterOperatorsValues['='],
          value: settings.facility,
        },
      ],
      excludes: [
        {
          field: 'locationType',
          operator: DatagridFilterOperatorsValues.isAnyOf,
          value: [StockLocationType.ESD_BIN, StockLocationType.BIN_SECTION],
        },
      ],
      noCache: true,
    }
  );

  const {
    data: stockLotLocationsByIdFirstHalfResponse,
    isFetching: isFetchingStockLotLocationsByIdFirstHalf,
    isLoading: isLoadingStockLotLocationsByIdFirstHalf,
    refetch: refetchStockLotLocationsByIdFirstHalf,
  } = useServerSideQuery<StockLocationSchema, WarehouseStockLocation>(
    useGetStockLocationsClientV2Query,
    firstHalfIds.length > 0
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [
            'id',
            'facility',
            'name',
            'externalName',
            'locationType',
            'parent',
          ],
          filters: [
            {
              field: 'id',
              operator: QueryFilterOperators.isAnyOf,
              value: firstHalfIds,
            },
            {
              field: 'locationType',
              operator: QueryFilterOperators.isAnyOf,
              value: [StockLocationType.ESD_BIN, StockLocationType.BIN_SECTION],
            },
          ],
          noCache: true,
        }
      : skipToken
  );

  const {
    data: stockLotLocationsByIdSecondHalfResponse,
    isFetching: isFetchingStockLotLocationsByIdSecondHalf,
    isLoading: isLoadingStockLotLocationsByIdSecondHalf,
    refetch: refetchStockLotLocationsByIdSecondHalf,
  } = useServerSideQuery<StockLocationSchema, WarehouseStockLocation>(
    useGetStockLocationsClientV2Query,
    secondHalfIds.length > 0
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [
            'id',
            'facility',
            'name',
            'externalName',
            'locationType',
            'parent',
          ],
          filters: [
            {
              field: 'id',
              operator: QueryFilterOperators.isAnyOf,
              value: secondHalfIds,
            },
            {
              field: 'locationType',
              operator: QueryFilterOperators.isAnyOf,
              value: [StockLocationType.ESD_BIN, StockLocationType.BIN_SECTION],
            },
          ],
          noCache: true,
        }
      : skipToken
  );

  const {
    data: stockLotLocationsByChildFirstHalfResponse,
    isFetching: isFetchingStockLotLocationsByChildFirstHalf,
    isLoading: isLoadingStockLotLocationsByChildFirstHalf,
    refetch: refetchStockLotLocationsByChildFirstHalf,
  } = useServerSideQuery<StockLocationSchema, WarehouseStockLocation>(
    useGetStockLocationsClientV2Query,
    // @ts-expect-error - reverse query lookup https://linear.app/cofactr/issue/UI-3999/figure-out-typing-for-reverse-lookups
    firstHalfIds.length > 0
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [
            'id',
            'facility',
            'name',
            'externalName',
            'locationType',
            'parent',
          ],
          filters: [
            {
              field: 'childLocation',
              operator: QueryFilterOperators.isAnyOf,
              value: firstHalfIds,
            },
            {
              field: 'locationType',
              operator: QueryFilterOperators.isAnyOf,
              value: [StockLocationType.ESD_BIN, StockLocationType.BIN_SECTION],
            },
          ],
          noCache: true,
        }
      : skipToken
  );

  // Second half - by childLocation
  const {
    data: stockLotLocationsByChildSecondHalfResponse,
    isFetching: isFetchingStockLotLocationsByChildSecondHalf,
    isLoading: isLoadingStockLotLocationsByChildSecondHalf,
    refetch: refetchStockLotLocationsByChildSecondHalf,
  } = useServerSideQuery<StockLocationSchema, WarehouseStockLocation>(
    useGetStockLocationsClientV2Query,
    // @ts-ignore - reverse query lookup https://linear.app/cofactr/issue/UI-3999/figure-out-typing-for-reverse-lookups
    secondHalfIds.length > 0
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [
            'id',
            'facility',
            'name',
            'externalName',
            'locationType',
            'parent',
          ],
          filters: [
            {
              field: 'childLocation',
              operator: QueryFilterOperators.isAnyOf,
              value: secondHalfIds,
            },
            {
              field: 'locationType',
              operator: QueryFilterOperators.isAnyOf,
              value: [StockLocationType.ESD_BIN, StockLocationType.BIN_SECTION],
            },
          ],
          noCache: true,
        }
      : skipToken
  );

  const stockLocations = useMemo(() => {
    const acc: Record<GenericResource['id'], WarehouseStockLocation> = {};

    if (stockLocationsResponse?.data) {
      stockLocationsResponse.data.forEach((location) => {
        acc[location.id] = location;
      });
    }

    if (stockLotLocationsByIdFirstHalfResponse?.data) {
      stockLotLocationsByIdFirstHalfResponse.data.forEach((location) => {
        acc[location.id] = location;
      });
    }

    if (stockLotLocationsByIdSecondHalfResponse?.data) {
      stockLotLocationsByIdSecondHalfResponse.data.forEach((location) => {
        acc[location.id] = location;
      });
    }

    if (stockLotLocationsByChildFirstHalfResponse?.data) {
      stockLotLocationsByChildFirstHalfResponse.data.forEach((location) => {
        acc[location.id] = location;
      });
    }

    if (stockLotLocationsByChildSecondHalfResponse?.data) {
      stockLotLocationsByChildSecondHalfResponse.data.forEach((location) => {
        acc[location.id] = location;
      });
    }

    return acc;
  }, [
    stockLocationsResponse,
    stockLotLocationsByIdFirstHalfResponse,
    stockLotLocationsByIdSecondHalfResponse,
    stockLotLocationsByChildFirstHalfResponse,
    stockLotLocationsByChildSecondHalfResponse,
  ]);

  const isFetching =
    isFetchingStockLocations ||
    isFetchingStockLotLocationsByIdFirstHalf ||
    isFetchingStockLotLocationsByIdSecondHalf ||
    isFetchingStockLotLocationsByChildFirstHalf ||
    isFetchingStockLotLocationsByChildSecondHalf;
  const isLoading =
    isLoadingStockLocations ||
    isLoadingStockLotLocationsByIdFirstHalf ||
    isLoadingStockLotLocationsByIdSecondHalf ||
    isLoadingStockLotLocationsByChildFirstHalf ||
    isLoadingStockLotLocationsByChildSecondHalf;

  const refetch = () => {
    refetchStockLocations();
    refetchStockLotLocationsByIdFirstHalf();
    refetchStockLotLocationsByIdSecondHalf();
    refetchStockLotLocationsByChildFirstHalf();
    refetchStockLotLocationsByChildSecondHalf();
  };

  return {
    stockLocations,
    isFetching,
    isLoading,
    refetch,
  };
};
